import type { CSSProperties } from "react";

export type ActionButtonTone = "orange" | "red" | "slate" | "emerald" | "sky";

const ACTION_BUTTON_STYLES: Record<ActionButtonTone, CSSProperties> = {
  orange: {
    backgroundImage: "linear-gradient(135deg,#f59e0b,#f97316)",
    boxShadow: "0 16px 36px rgba(249,115,22,0.28)",
    borderColor: "rgba(255,255,255,0.32)",
  },
  red: {
    backgroundImage: "linear-gradient(135deg,#fb7185,#ef4444)",
    boxShadow: "0 16px 36px rgba(239,68,68,0.26)",
    borderColor: "rgba(255,255,255,0.3)",
  },
  slate: {
    backgroundImage: "linear-gradient(135deg,#64748b,#475569)",
    boxShadow: "0 16px 36px rgba(71,85,105,0.24)",
    borderColor: "rgba(255,255,255,0.3)",
  },
  emerald: {
    backgroundImage: "linear-gradient(135deg,#34d399,#10b981)",
    boxShadow: "0 16px 36px rgba(16,185,129,0.24)",
    borderColor: "rgba(255,255,255,0.3)",
  },
  sky: {
    backgroundImage: "linear-gradient(135deg,#38bdf8,#0ea5e9)",
    boxShadow: "0 16px 36px rgba(14,165,233,0.28)",
    borderColor: "rgba(255,255,255,0.32)",
  },
};

const clampChannel = (value: number) => Math.min(255, Math.max(0, value));

const parseHexColor = (color: string) => {
  if (!/^#[\da-fA-F]{6}$/.test(color)) return null;

  return {
    r: parseInt(color.slice(1, 3), 16),
    g: parseInt(color.slice(3, 5), 16),
    b: parseInt(color.slice(5, 7), 16),
  };
};

const toHexColor = ({
  r,
  g,
  b,
}: {
  r: number;
  g: number;
  b: number;
}) =>
  `#${clampChannel(r).toString(16).padStart(2, "0")}${clampChannel(g)
    .toString(16)
    .padStart(2, "0")}${clampChannel(b).toString(16).padStart(2, "0")}`;

const mixRgb = (
  source: { r: number; g: number; b: number },
  target: { r: number; g: number; b: number },
  weight: number,
) => ({
  r: Math.round(source.r + (target.r - source.r) * weight),
  g: Math.round(source.g + (target.g - source.g) * weight),
  b: Math.round(source.b + (target.b - source.b) * weight),
});

export const ACTION_BUTTON_TEXT_CLASS_NAME =
  "border text-sm font-semibold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60";

export const ACTION_BUTTON_NEUTRAL_CLASS_NAME =
  "border border-slate-200/80 bg-white/85 text-slate-600 shadow-[0_10px_24px_rgba(148,163,184,0.12)] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60";

export const getActionButtonStyle = (
  tone: ActionButtonTone,
): CSSProperties => ACTION_BUTTON_STYLES[tone];

export const getAdaptiveActionButtonStyle = (
  color: string,
): CSSProperties => {
  const rgb = parseHexColor(color);
  if (!rgb) return getActionButtonStyle("orange");

  const gradientStart = toHexColor(mixRgb(rgb, { r: 255, g: 255, b: 255 }, 0.18));
  const gradientEnd = toHexColor(mixRgb(rgb, { r: 0, g: 0, b: 0 }, 0.1));

  return {
    backgroundImage: `linear-gradient(135deg,${gradientStart},${gradientEnd})`,
    boxShadow: `0 16px 36px rgba(${rgb.r},${rgb.g},${rgb.b},0.28)`,
    borderColor: "rgba(255,255,255,0.32)",
  };
};
